import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { HTTPError } from 'ky';

import { useWmgSession } from '~/modules/auth/hooks/useWmgSession';
import { LabelGroupQueryKeys } from '~settings/api/config/queryKeys';
import { BFFClient } from '~settings/api/lib/BFFClient';
import { getRequestHeaders } from '~settings/api/lib/headers';
import type { LabelGroupDetail } from '~settings/api/lib/schemas';
import type {
  LabelGroupPayload,
  PresetUseMutationOptions,
} from '~settings/api/lib/types';

export const useLabelGroupEdit = (
  labelGroupId: string,
  options?: PresetUseMutationOptions<LabelGroupDetail, LabelGroupPayload>,
) => {
  const { token } = useWmgSession();
  const queryClient = useQueryClient();

  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation<LabelGroupDetail, HTTPError, LabelGroupPayload>({
    mutationFn: async (data: LabelGroupPayload) => {
      return BFFClient.put(`label-groups/${labelGroupId}`, {
        json: data,
        headers: getRequestHeaders(token),
      }).json();
    },

    onSuccess: async (data: LabelGroupDetail, variables, ctx) => {
      await queryClient.invalidateQueries({
        queryKey: LabelGroupQueryKeys.list({}),
      });
      queryClient.removeQueries({
        queryKey: LabelGroupQueryKeys.detail({ labelGroupId }),
      });

      if (onSuccess) {
        onSuccess(data, variables, ctx);
      }
    },
    onError: (error) => {
      console.error('Edit label group failed:', error);
    },
    ...restOptions,
  });
};
